import { type LexiMood, WordsHappenButton, WordsHappenFormAlert } from '@words-happen/ui-components';
import type React from 'react';
import { useId } from 'react';
import type { VerificationLinkState } from '../../api';
import { MessageScreen, messageScreenActionsClassName, messageScreenStatusClassName } from '../../components';
import type { Resend } from '../use-resend';

/** What EmailVerifiedScreen shows: the link's outcome, or that it is being checked or couldn't be. */
export type EmailVerifiedState = VerificationLinkState | 'checking' | 'failed';

/** Who is signed in on this device: decides how the player carries on. */
export type PlayerStatus = 'verified' | 'unverified' | 'none';

export interface EmailVerifiedScreenProps {
  state: EmailVerifiedState;
  player: PlayerStatus;
  /** "Resend email", offered on an expired or invalid link to a signed-in, unverified player. */
  resend: Resend;
  /** Why the link couldn't be checked, for the `failed` state. */
  error?: string;
  /** Continue (handoff, or on to /verify-email), or sign in when no one is signed in. */
  onContinue: () => void;
  /** Checks the link again, for the `failed` state. */
  onRetry: () => void;
}

interface Copy {
  title: string;
  mood: LexiMood;
  body: string;
}

const COPY: Readonly<Record<EmailVerifiedState, Copy>> = {
  checking: { title: 'Verifying your email…', mood: 'focused', body: 'One moment.' },
  verified: { title: 'Your email is verified', mood: 'excited', body: "You're all set." },
  'already-used': {
    title: 'This link has already been used',
    mood: 'happy',
    body: 'Your email address is already verified.',
  },
  expired: {
    title: 'This link has expired',
    mood: 'surprised',
    body: 'Verification links only work for a limited time.',
  },
  invalid: {
    title: "This link doesn't work",
    mood: 'surprised',
    body: 'It may be incomplete or out of date. Use the link in the latest email we sent you.',
  },
  failed: { title: "We couldn't check your link", mood: 'surprised', body: 'Your link may still be good.' },
};

/** What to do next, for a link that verified nothing new. */
const nextStep = (state: EmailVerifiedState, player: PlayerStatus): string | undefined => {
  if (state !== 'expired' && state !== 'invalid') {
    return player === 'none' ? 'Sign in to start playing.' : undefined;
  }
  switch (player) {
    case 'unverified':
      return 'We can send you a new one.';
    case 'none':
      return 'Sign in and we can send you a new one.';
    default:
      return undefined;
  }
};

/**
 * The outcome of opening a verification link (/verify-email/confirm): verified, expired, already used or invalid.
 * Each offers one way on: continue to the game, resend the email, or sign in.
 */
export const EmailVerifiedScreen: React.FC<EmailVerifiedScreenProps> = ({
  state,
  player,
  resend,
  error,
  onContinue,
  onRetry,
}) => {
  const cooldownId = useId();
  const { title, mood, body } = COPY[state];
  const step = nextStep(state, player);
  const offersResend = (state === 'expired' || state === 'invalid') && player === 'unverified';
  const alert = state === 'failed' ? error : offersResend ? resend.error : undefined;

  const action = () => {
    if (state === 'checking') {
      return null;
    }
    if (state === 'failed') {
      return (
        <WordsHappenButton variant="coral" size="lg" onClick={onRetry}>
          Try again
        </WordsHappenButton>
      );
    }
    if (offersResend) {
      return (
        <>
          <WordsHappenButton
            variant="coral"
            size="lg"
            loading={resend.sending}
            disabled={resend.cooldown > 0}
            aria-describedby={resend.cooldown > 0 ? cooldownId : undefined}
            onClick={resend.resend}
          >
            Resend email
          </WordsHappenButton>
          {resend.cooldown > 0 ? (
            <p id={cooldownId} className={messageScreenStatusClassName}>
              You can resend in {resend.cooldown}s
            </p>
          ) : null}
        </>
      );
    }
    return (
      <WordsHappenButton variant="coral" size="lg" onClick={onContinue}>
        {player === 'none' ? 'Sign in' : 'Continue'}
      </WordsHappenButton>
    );
  };

  return (
    <MessageScreen
      title={title}
      mood={mood}
      action={
        <div className={messageScreenActionsClassName}>
          {alert ? <WordsHappenFormAlert>{alert}</WordsHappenFormAlert> : null}
          {action()}
          <p role="status" className={messageScreenStatusClassName}>
            {offersResend ? resend.status : null}
          </p>
        </div>
      }
    >
      <p role={state === 'checking' ? 'status' : undefined}>
        {body}
        {step ? ` ${step}` : null}
      </p>
    </MessageScreen>
  );
};
